import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_VERSION } from '@/lib/native/version';
import type { PushPermission } from '@/lib/push';

import { useDevicesDeps } from './context';
import {
  useEnablePush,
  useForgetDevice,
  useMyDevices,
  usePushPermission,
} from './queries';
import type { Device, DisabledReason } from './schemas';
import { isActive } from './schemas';
import { NOTIFICATIONS_NAMESPACE } from './strings';

/**
 * BP-11 §6's `/settings/notifications`: `PushToggle`, `DeviceList`, and a
 * link to the consent screen.
 *
 * **Four states, not two** (§6: "denied (OS-level, with 'open settings'),
 * granted, unsupported (web)" — plus the one before anybody has been
 * asked). They are genuinely different situations and only one of them has
 * a button that helps: `prompt` can ask, `denied` can only be fixed in the
 * settings app, `granted` has nothing to do, and the browser cannot
 * receive a push at all.
 *
 * **Consent is named but not asked for here.** Rule R7 gates push on
 * `consent(purpose='reminders')` and BP-03 owns that screen; duplicating
 * the toggle would give a person two switches for one thing and no way to
 * tell which one is off.
 */
export function NotificationsPage(): JSX.Element {
  const { t } = useTranslation(NOTIFICATIONS_NAMESPACE);
  const devices = useMyDevices();

  return (
    <section data-testid="notifications-page">
      <h1 className="mb-2 text-lg font-semibold">{t('title')}</h1>
      <p className="mb-4 text-sm text-muted-foreground">{t('intro')}</p>
      <PushToggle />
      <p className="mt-4 text-sm text-muted-foreground" data-testid="notifications-consent">
        {t('consent')}
      </p>
      <DeviceList devices={devices.data ?? []} pending={devices.isPending} />
      <p className="mt-6 text-xs text-muted-foreground" data-testid="app-version">
        {t('version', { version: APP_VERSION })}
      </p>
    </section>
  );
}

/** §6's `PushToggle` — one of four states, and at most one button. */
export function PushToggle(): JSX.Element {
  const { t } = useTranslation(NOTIFICATIONS_NAMESPACE);
  const { openSettings } = useDevicesDeps();
  const permission = usePushPermission();
  const enable = useEnablePush();
  const state: PushPermission = permission.data ?? 'prompt';

  return (
    <div className="rounded border border-border p-3" data-testid={`push-${state}`}>
      <p className="text-sm">{t(`state.${state}`)}</p>
      {state === 'prompt' && (
        <button
          type="button"
          data-testid="push-enable"
          className="mt-2 rounded bg-primary px-3 py-1 text-sm text-primary-foreground disabled:opacity-50"
          disabled={enable.isPending}
          onClick={() => {
            enable.mutate();
          }}
        >
          {enable.isPending ? t('action.enabling') : t('action.enable')}
        </button>
      )}
      {state === 'denied' && (
        <>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="push-denied-help">
            {t('deniedHelp')}
          </p>
          {openSettings !== undefined && (
            <button
              type="button"
              data-testid="push-settings"
              className="mt-2 rounded border border-border px-3 py-1 text-sm"
              onClick={() => {
                void openSettings();
              }}
            >
              {t('action.settings')}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/** §6's `DeviceList`. Disabled phones are shown, with the reason. */
export function DeviceList({
  devices,
  pending = false,
}: {
  devices: Device[];
  pending?: boolean;
}): JSX.Element {
  const { t } = useTranslation(NOTIFICATIONS_NAMESPACE);
  const forget = useForgetDevice();

  if (pending) {
    return (
      <p role="status" data-testid="devices-loading" className="mt-4 text-sm text-muted-foreground">
        …
      </p>
    );
  }
  return (
    <section className="mt-4">
      <h2 className="mb-2 text-base font-semibold">{t('devices.title')}</h2>
      {devices.length === 0 ? (
        <p data-testid="devices-empty" className="text-sm text-muted-foreground">
          {t('devices.empty')}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {devices.map((device) => (
            <li key={device.id} className="py-2" data-testid={`device-${device.id}`}>
              <p className="text-sm">
                {device.platform} · …{device.token_tail}
              </p>
              {!isActive(device) && device.disabled_reason !== null && (
                <p
                  className="text-sm text-muted-foreground"
                  data-testid={`device-${device.id}-reason`}
                >
                  {t(`devices.disabled.${device.disabled_reason satisfies DisabledReason}`)}
                </p>
              )}
              <button
                type="button"
                data-testid={`device-${device.id}-forget`}
                className="mt-1 text-sm underline disabled:opacity-50"
                disabled={forget.isPending}
                onClick={() => {
                  forget.mutate(device.id);
                }}
              >
                {forget.isPending ? t('action.forgetting') : t('action.forget')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
